/* Seller Credit Panel (Phase SC-3)
 *
 * Mounted inside SellerWallet under the `#credit` hash tab.
 * Phase SC-3 changes (Feb 2026):
 *   1. No more apply form — every seller is auto-issued a starter line
 *      (Building $25k → Excellent $250k by trust score) at signup.
 *   2. Hero adds a "Request limit increase" CTA + pricing-tiers card.
 *   3. Eligible / History tables are paginated + collapsible accordions.
 */
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { money } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../../components/ui/dialog";
import { Donut } from "../../components/Sparkline";
import {
  Sparkles, ShieldCheck, Clock, AlertTriangle, Building2, Zap, FileText,
  Search, ChevronDown, ChevronRight, ChevronLeft, ChevronsRight, TrendingUp,
  Lock, Gauge, Award,
} from "lucide-react";
import { toast } from "sonner";
import SellInvoiceModal from "../../components/SellInvoiceModal";
import AutoFundRulesSection from "../../components/AutoFundRulesSection";

const FEE_BY_TERM = {
  net14:  0.0075,
  net30:  0.0100,
  net60:  0.0150,
  net90:  0.0200,
  net120: 0.0250,
};
const PAGE_SIZE = 10;

export default function SellerCreditPanel() {
  const [loading, setLoading] = useState(true);
  const [line, setLine] = useState(null);
  const [pendingApp, setPendingApp] = useState(null);
  const [summary, setSummary] = useState(null);
  const [purchases, setPurchases] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [sellTarget, setSellTarget] = useState(null);
  const [increaseOpen, setIncreaseOpen] = useState(false);
  const [eligibility, setEligibility] = useState(null);
  const [factoringScore, setFactoringScore] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [l, s, p, inv, elig, fscore] = await Promise.all([
        api.get("/seller/credit/line"),
        api.get("/seller/credit/summary"),
        api.get("/seller/credit/purchases", { params: { limit: 200 } }),
        api.get("/invoices"),
        api.get("/seller/factoring/eligibility-check").catch(() => ({ data: null })),
        api.get("/seller/factoring-score").catch(() => ({ data: null })),
      ]);
      setLine(l.data.line);
      setPendingApp(l.data.pending_app);
      setSummary(s.data);
      setPurchases(p.data.rows || []);
      setInvoices(inv.data || []);
      setEligibility(elig.data);
      setFactoringScore(fscore.data);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  if (loading) return <div className="p-8 text-foreground/50 text-sm">Loading credit profile…</div>;

  /* Edge case: lender_partner not configured → line couldn't be auto-issued */
  if (!line) {
    return (
      <div className="rounded-2xl border border-amber-200 bg-amber-50 p-6 text-sm text-amber-900" data-testid="seller-credit-unavailable">
        <div className="flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 mt-0.5 shrink-0 text-amber-600"/>
          <div>
            <div className="display text-base text-amber-950">Credit financing temporarily unavailable</div>
            <p className="mt-1 leading-relaxed">No lender partner is configured for your region yet. We're working on it — your account is already cleared to factor invoices the moment a lender goes live.</p>
          </div>
        </div>
      </div>
    );
  }

  const util = summary?.utilization_pct ?? 0;
  const tier = line?.starter_tier || "—";
  const tierBadge = tier === "excellent" ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                  : tier === "good"      ? "bg-violet-50 text-violet-700 border-violet-200"
                  : tier === "fair"      ? "bg-amber-50 text-amber-700 border-amber-200"
                  :                        "bg-secondary text-foreground/70 border-border";

  return (
    <div className="space-y-6" data-testid="seller-credit-active">
      {pendingApp && (
        <div className="rounded-xl border border-amber-200 bg-amber-50 px-5 py-3.5 flex items-start gap-3" data-testid="seller-credit-pending-increase">
          <Clock className="w-4.5 h-4.5 mt-0.5 shrink-0 text-amber-600"/>
          <div className="text-[13px] text-amber-900">
            <span className="font-medium">Limit-increase request pending</span> — submitted {new Date(pendingApp.submitted_at).toLocaleDateString()} to <span className="font-medium">{pendingApp.lender_org_name}</span>. Requested {money(pendingApp.requested_limit_usd)} (currently {money(pendingApp.current_limit_usd || line.limit_usd)}). You'll be notified when there's a decision.
          </div>
        </div>
      )}

      {/* Phase SC-5.3 · Eligibility gate panel (only when blocked) */}
      {eligibility && !eligibility.eligible && (
        <EligibilityGatePanel gate={eligibility} onChange={load}/>
      )}

      <div className="grid lg:grid-cols-3 gap-5">
        {/* Hero */}
        <div className="rounded-2xl p-7 relative overflow-hidden text-background bg-gradient-to-br from-foreground to-foreground/85" data-testid="seller-credit-summary">
          <div className="absolute -right-10 -top-10 w-40 h-40 rounded-full bg-accent/25 blur-3xl"/>
          <div className="relative flex items-center gap-6">
            <Donut value={util} max={100} size={120} stroke={12}
              color="#E8FF3A" track="rgba(232,255,58,0.15)"/>
            <div>
              <div className="text-[10px] mono uppercase tracking-[0.2em] text-accent/80">Available</div>
              <div className="display text-4xl mt-1">{money(summary?.available_usd ?? 0)}</div>
              <div className="text-[11px] text-background/55 mt-1">of {money(summary?.limit_usd ?? 0)} limit</div>
              <div className="mt-3 inline-flex chip bg-accent/30 border border-accent/40 text-background">{util}% utilized</div>
            </div>
          </div>
          <div className="relative mt-6 grid grid-cols-2 gap-3 text-xs">
            <div><div className="text-background/50">Advanced</div><div className="display text-base">{money(summary?.used_usd ?? 0)}</div></div>
            <div><div className="text-background/50">Lifetime</div><div className="display text-base">{money(summary?.lifetime_advanced_usd ?? 0)}</div></div>
            <div><div className="text-background/50">Open</div><div className="display text-base">{summary?.funded_count ?? 0}</div></div>
            <div><div className="text-background/50">Repaid</div><div className="display text-base">{summary?.repaid_count ?? 0}</div></div>
          </div>
          <Button onClick={() => setIncreaseOpen(true)} variant="outline"
            data-testid="seller-credit-increase-btn"
            className="relative mt-5 w-full rounded-full bg-accent/15 border-accent/40 text-background hover:bg-accent/25">
            <TrendingUp className="w-4 h-4 mr-1.5"/>Request limit increase
          </Button>
        </div>

        {/* Pricing tiers + lender info */}
        <div className="lg:col-span-2 rounded-2xl bg-white border border-border p-7">
          <div className="flex items-center justify-between mb-2">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <Building2 className="w-4 h-4 text-foreground/70"/>
                <div className="text-[10px] mono uppercase tracking-[0.2em] text-foreground/50">Lender</div>
              </div>
              <div className="display text-xl tracking-tight">{summary?.lender_org_name || line?.lender_org_name || "—"}</div>
            </div>
            {tier !== "—" && (
              <div className={`chip text-[10.5px] border uppercase tracking-wider ${tierBadge}`} data-testid="seller-credit-tier">
                {tier} tier · starter
              </div>
            )}
          </div>
          <p className="text-sm text-foreground/60 mt-3 leading-relaxed">
            Auto-issued at signup. Fee scales by term — longer terms cost slightly more because the lender carries the receivable longer.
          </p>

          {/* Pricing-by-term card */}
          <div className="mt-4 grid grid-cols-5 gap-2" data-testid="seller-credit-pricing-tiers">
            {[
              { t: "net14",  label: "Net-14"  },
              { t: "net30",  label: "Net-30"  },
              { t: "net60",  label: "Net-60"  },
              { t: "net90",  label: "Net-90"  },
              { t: "net120", label: "Net-120" },
            ].map(({ t, label }) => (
              <div key={t} className="rounded-lg bg-secondary/50 border border-border px-2.5 py-2.5"
                   data-testid={`pricing-tier-${t}`}>
                <div className="text-foreground/55 mono uppercase tracking-wider text-[9.5px]">{label}</div>
                <div className="display text-sm mt-0.5 tabular-nums">{(FEE_BY_TERM[t] * 100).toFixed(2)}%</div>
                <div className="text-[9.5px] text-foreground/45 mono mt-0.5">${(10000 * FEE_BY_TERM[t]).toFixed(0)}/10k</div>
              </div>
            ))}
          </div>

          <div className="mt-4 flex items-center gap-3 text-[11.5px] text-foreground/55">
            <ShieldCheck className="w-3.5 h-3.5 text-emerald-600"/>
            Allianz Trade insures the buyer side · {(summary?.advance_rate ? summary.advance_rate * 100 : 95).toFixed(0)}% advance on every approved invoice
          </div>
        </div>
      </div>

      {/* Phase SC-5.3 · Factoring score breakdown */}
      {factoringScore && (
        <FactoringScorePanel score={factoringScore}/>
      )}

      {/* Eligible invoices · paginated accordion */}
      <EligibleInvoicesSection
        invoices={invoices}
        summary={summary}
        onSell={setSellTarget}
      />

      {/* Per-buyer auto-fund */}
      <AutoFundRulesSection/>

      {/* History · paginated accordion */}
      <HistorySection purchases={purchases}/>

      <SellInvoiceModal
        open={!!sellTarget}
        invoice={sellTarget}
        onClose={() => setSellTarget(null)}
        onSuccess={() => load()}
      />

      <RequestIncreaseDialog
        open={increaseOpen}
        onClose={() => setIncreaseOpen(false)}
        currentLimit={Number(line?.limit_usd || 0)}
        onSubmitted={() => { setIncreaseOpen(false); load(); }}
      />
    </div>
  );
}

/* ─── Eligible Invoices section · paginated + filterable ────────────── */
function EligibleInvoicesSection({ invoices, summary, onSell }) {
  const [open, setOpen] = useState(true);
  const [search, setSearch] = useState("");
  const [termFilter, setTermFilter] = useState("all");
  const [page, setPage] = useState(0);

  const { eligible, pendingShip } = useMemo(() => {
    const valid = invoices.filter(i => {
      const t = (i.terms || "").toLowerCase();
      return ["net14", "net30", "net60", "net90", "net120", "custom"].includes(t)
        && !i.sold_to_lender_org_id
        && !["paid", "void", "cancelled"].includes(i.status)
        && Number(i.amount || 0) > 0;
    });
    return {
      eligible:    valid.filter(i => i.carrier_shipped !== false),
      pendingShip: valid.filter(i => i.carrier_shipped === false),
    };
  }, [invoices]);

  const filtered = useMemo(() => {
    const s = search.trim().toLowerCase();
    return eligible.filter(i => {
      if (termFilter !== "all" && (i.terms || "").toLowerCase() !== termFilter) return false;
      if (!s) return true;
      return (
        (i.code || "").toLowerCase().includes(s) ||
        (i.buyer_name || "").toLowerCase().includes(s) ||
        String(i.amount || "").includes(s)
      );
    });
  }, [eligible, search, termFilter]);

  // Reset to first page when filter changes
  useEffect(() => { setPage(0); }, [search, termFilter]);

  const pageCount = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage = Math.min(page, pageCount - 1);
  const slice = filtered.slice(safePage * PAGE_SIZE, (safePage + 1) * PAGE_SIZE);

  const advanceRate = summary?.advance_rate ? Number(summary.advance_rate) : 0.95;
  const feeForTerm = (t) => FEE_BY_TERM[(t || "").toLowerCase()] ?? 0.015;

  if (eligible.length === 0 && pendingShip.length === 0) {
    return (
      <div className="rounded-2xl border border-dashed border-border bg-secondary/40 p-10 text-center" data-testid="seller-credit-no-invoices">
        <FileText className="w-10 h-10 mx-auto text-foreground/30 mb-3"/>
        <h3 className="display text-lg">No financeable invoices yet</h3>
        <p className="text-sm text-foreground/55 mt-1.5 max-w-md mx-auto leading-relaxed">
          Invoices with buyer-accepted Net-X terms will appear here once the carrier has confirmed shipment. One click on
          <span className="font-medium"> Get paid today </span> and the cash lands in your wallet in seconds.
        </p>
      </div>
    );
  }

  return (
    <div className="rounded-2xl bg-white border border-border overflow-hidden" data-testid="seller-credit-eligible-invoices">
      <button
        onClick={() => setOpen(o => !o)}
        data-testid="seller-credit-eligible-toggle"
        className="w-full px-5 py-4 border-b border-border flex items-center justify-between gap-3 text-left hover:bg-secondary/30 transition-colors"
      >
        <div>
          <div className="text-[10px] mono uppercase tracking-[0.2em] text-lime-700 inline-flex items-center gap-1.5">
            <Sparkles className="w-3 h-3"/> Pre-approved · one click to fund
          </div>
          <h3 className="display text-lg mt-1">Get paid today on these invoices</h3>
          <p className="text-[12px] text-foreground/55 mt-0.5">
            Buyer already accepted Net-X terms · your credit limit covers it · click and the cash lands in seconds.
          </p>
        </div>
        <div className="flex items-center gap-3 shrink-0">
          <span className="chip bg-lime-50 text-lime-800 border border-lime-200 text-[11px]">
            {eligible.length} eligible
          </span>
          {open ? <ChevronDown className="w-4 h-4 text-foreground/50"/> : <ChevronRight className="w-4 h-4 text-foreground/50"/>}
        </div>
      </button>

      {open && (
        <>
          <div className="px-5 py-3 border-b border-border flex items-center gap-2.5 bg-secondary/20">
            <div className="relative flex-1 max-w-sm">
              <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-foreground/40"/>
              <Input
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder="Search invoice, buyer, amount…"
                data-testid="eligible-search"
                className="h-9 pl-9 text-[13px]"
              />
            </div>
            <select
              value={termFilter}
              onChange={e => setTermFilter(e.target.value)}
              data-testid="eligible-term-filter"
              className="h-9 rounded-md border border-input bg-background px-2.5 text-[12.5px]"
            >
              <option value="all">All terms</option>
              <option value="net14">Net-14</option>
              <option value="net30">Net-30</option>
              <option value="net60">Net-60</option>
              <option value="net90">Net-90</option>
              <option value="net120">Net-120</option>
            </select>
            <div className="ml-auto text-[11.5px] text-foreground/55 mono" data-testid="eligible-counts">
              {filtered.length === eligible.length
                ? `${eligible.length} total`
                : `${filtered.length} of ${eligible.length}`}
            </div>
          </div>

          {slice.length === 0 ? (
            <div className="p-10 text-center text-sm text-foreground/55">
              No invoices match your search.
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-secondary/40 text-left text-[11px] uppercase tracking-wider text-foreground/55">
                <tr>
                  <th className="px-5 py-2.5 font-medium">Invoice</th>
                  <th className="px-5 py-2.5 font-medium">Buyer</th>
                  <th className="px-5 py-2.5 font-medium">Amount</th>
                  <th className="px-5 py-2.5 font-medium">Terms</th>
                  <th className="px-5 py-2.5 font-medium">You receive now</th>
                  <th className="px-5 py-2.5 font-medium">Fee</th>
                  <th className="px-5 py-2.5"/>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {slice.map(i => {
                  const amount = Number(i.amount || 0);
                  const fee = feeForTerm(i.terms);
                  const advance = Math.round(amount * advanceRate * 100) / 100;
                  const feeUsd = Math.round(amount * fee * 100) / 100;
                  return (
                    <tr key={i.id} data-testid={`eligible-inv-${i.code}`}>
                      <td className="px-5 py-3.5 mono text-[12.5px]">{i.code}</td>
                      <td className="px-5 py-3.5 text-foreground/75">{i.buyer_name || "—"}</td>
                      <td className="px-5 py-3.5 display tabular-nums">{money(amount)}</td>
                      <td className="px-5 py-3.5 uppercase text-[11px]">
                        <span className="chip bg-secondary text-foreground/70 text-[10.5px]">{i.terms}</span>
                        {(i.terms_start_event || "ship_date") === "ship_date" && (
                          <span data-testid={`scp-clock-running-${i.code}`} className="chip ml-1 bg-emerald-50 text-emerald-700 border border-emerald-200 text-[10px] normal-case tracking-normal">
                            <Clock className="w-2.5 h-2.5"/>Clock running
                          </span>
                        )}
                      </td>
                      <td className="px-5 py-3.5 display tabular-nums text-lime-700">{money(advance)}</td>
                      <td className="px-5 py-3.5 tabular-nums text-foreground/55 text-[12.5px]">
                        −{money(feeUsd)} <span className="text-foreground/40">({(fee * 100).toFixed(2)}%)</span>
                      </td>
                      <td className="px-5 py-3.5 text-right">
                        <Button size="sm"
                          onClick={() => onSell(i)}
                          data-testid={`credit-page-sell-${i.code}`}
                          className="rounded-full bg-lime-400 text-slate-900 hover:bg-lime-500">
                          <Zap className="w-3.5 h-3.5 mr-1"/>Get paid today
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}

          {filtered.length > PAGE_SIZE && (
            <Pagination page={safePage} pageCount={pageCount} onChange={setPage} testid="eligible-pager"/>
          )}
        </>
      )}

      {/* Pending shipment — un-anchored eligible-by-terms invoices */}
      {pendingShip.length > 0 && (
        <div className="border-t-2 border-dashed border-border bg-amber-50/30 px-5 py-4" data-testid="seller-credit-pending-ship">
          <div className="flex items-center gap-2 mb-2">
            <Clock className="w-4 h-4 text-amber-600 shrink-0"/>
            <div>
              <div className="text-[11px] mono uppercase tracking-[0.18em] text-amber-700">Awaiting carrier movement</div>
              <div className="text-[12.5px] text-foreground/70 mt-0.5">{pendingShip.length} invoice{pendingShip.length === 1 ? "" : "s"} with buyer terms — funds release the moment the carrier scans them in transit.</div>
            </div>
          </div>
          <table className="w-full text-[12.5px] mt-2">
            <tbody className="divide-y divide-border/60">
              {pendingShip.slice(0, 5).map(i => (
                <tr key={i.id} data-testid={`pending-ship-${i.code}`} className="text-foreground/75">
                  <td className="py-1.5 mono">{i.code}</td>
                  <td className="py-1.5">{i.buyer_name || "—"}</td>
                  <td className="py-1.5 tabular-nums">{money(i.amount)}</td>
                  <td className="py-1.5 uppercase text-[10.5px]">
                    <span className="chip bg-secondary text-foreground/65 text-[10px]">{i.terms}</span>
                  </td>
                  <td className="py-1.5 text-right">
                    <span className="chip bg-amber-50 text-amber-800 border border-amber-200 text-[10px]">Pending shipment</span>
                  </td>
                </tr>
              ))}
              {pendingShip.length > 5 && (
                <tr><td colSpan={5} className="py-2 text-center text-[11.5px] text-foreground/50">+ {pendingShip.length - 5} more</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/* ─── History section · paginated accordion ─────────────────────────── */
function HistorySection({ purchases }) {
  // Auto-expand the accordion when the seller has activity — otherwise
  // first-time users miss the open-loan table on landing (the "4 TOTAL"
  // badge isn't loud enough on its own). Empty state stays collapsed.
  const [open, setOpen] = useState(purchases.length > 0);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [page, setPage] = useState(0);

  const filtered = useMemo(() => {
    const s = search.trim().toLowerCase();
    return purchases.filter(p => {
      if (statusFilter !== "all") {
        if (statusFilter === "past_due" && !((p.status || "").startsWith("past_due"))) return false;
        if (statusFilter !== "past_due" && p.status !== statusFilter) return false;
      }
      if (!s) return true;
      return (
        (p.invoice_code || "").toLowerCase().includes(s) ||
        String(p.advance_amount || "").includes(s)
      );
    });
  }, [purchases, search, statusFilter]);

  useEffect(() => { setPage(0); }, [search, statusFilter]);

  const pageCount = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage = Math.min(page, pageCount - 1);
  const slice = filtered.slice(safePage * PAGE_SIZE, (safePage + 1) * PAGE_SIZE);

  return (
    <div className="rounded-2xl bg-white border border-border overflow-hidden" data-testid="seller-credit-history">
      <button
        onClick={() => setOpen(o => !o)}
        data-testid="seller-credit-history-toggle"
        className="w-full px-5 py-4 border-b border-border flex items-center justify-between text-left hover:bg-secondary/30 transition-colors"
      >
        <div>
          <div className="text-[10px] mono uppercase tracking-[0.2em] text-foreground/50">History</div>
          <h3 className="display text-lg mt-1">Sold invoices</h3>
        </div>
        <div className="flex items-center gap-3 shrink-0">
          <span className="chip bg-secondary text-foreground/70 text-[11px]">
            {purchases.length} total
          </span>
          {open ? <ChevronDown className="w-4 h-4 text-foreground/50"/> : <ChevronRight className="w-4 h-4 text-foreground/50"/>}
        </div>
      </button>

      {open && (purchases.length === 0 ? (
        <div className="p-10 text-center text-sm text-foreground/55">
          No invoices sold yet. When a buyer confirms Net-X terms on one of your invoices, the <span className="mono">"Get paid today"</span> CTA appears on that invoice.
        </div>
      ) : (
        <>
          <div className="px-5 py-3 border-b border-border flex items-center gap-2.5 bg-secondary/20">
            <div className="relative flex-1 max-w-sm">
              <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-foreground/40"/>
              <Input
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder="Search invoice code, amount…"
                data-testid="history-search"
                className="h-9 pl-9 text-[13px]"
              />
            </div>
            <select
              value={statusFilter}
              onChange={e => setStatusFilter(e.target.value)}
              data-testid="history-status-filter"
              className="h-9 rounded-md border border-input bg-background px-2.5 text-[12.5px]"
            >
              <option value="all">All statuses</option>
              <option value="funded">Funded</option>
              <option value="repaid">Repaid</option>
              <option value="past_due">Past due</option>
            </select>
            <div className="ml-auto text-[11.5px] text-foreground/55 mono">
              {filtered.length === purchases.length
                ? `${purchases.length} total`
                : `${filtered.length} of ${purchases.length}`}
            </div>
          </div>

          {slice.length === 0 ? (
            <div className="p-10 text-center text-sm text-foreground/55">
              No purchases match your search.
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-secondary/40 text-left text-[11px] uppercase tracking-wider text-foreground/55">
                <tr>
                  <th className="px-5 py-2.5 font-medium">Invoice</th>
                  <th className="px-5 py-2.5 font-medium">Advanced</th>
                  <th className="px-5 py-2.5 font-medium">Reserve</th>
                  <th className="px-5 py-2.5 font-medium">Fee</th>
                  <th className="px-5 py-2.5 font-medium">Terms</th>
                  <th className="px-5 py-2.5 font-medium">Due</th>
                  <th className="px-5 py-2.5 font-medium">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {slice.map(p => (
                  <tr key={p.id} data-testid={`seller-credit-row-${p.invoice_code}`}>
                    <td className="px-5 py-3 mono">
                      {p.invoice_code || (p.invoice_id || "").slice(0, 8)}
                      {p.auto_funded && (
                        <span className="ml-1.5 chip bg-violet-50 text-violet-700 border border-violet-200 text-[9.5px] inline-flex items-center gap-0.5" data-testid={`auto-badge-${p.invoice_code}`}>
                          <Zap className="w-2.5 h-2.5"/>Auto
                        </span>
                      )}
                    </td>
                    <td className="px-5 py-3 display">{money(p.advance_amount)}</td>
                    <td className="px-5 py-3 text-foreground/65">{money(p.reserve_amount)}</td>
                    <td className="px-5 py-3 text-foreground/65">{money(p.fee_usd)}</td>
                    <td className="px-5 py-3 uppercase text-xs">{p.terms}</td>
                    <td className="px-5 py-3 text-xs text-foreground/60">
                      {p.due_date ? new Date(p.due_date).toLocaleDateString() : "—"}
                      {p.days_past_due > 0 && (
                        <span className="ml-1.5 chip bg-rose-50 text-rose-700 border border-rose-200 text-[10px]">+{p.days_past_due}d</span>
                      )}
                    </td>
                    <td className="px-5 py-3">
                      <span className={`chip text-[10.5px] ${
                        p.status === "repaid" ? "bg-emerald-50 text-emerald-700 border border-emerald-200" :
                        p.status === "funded" ? "bg-violet-50 text-violet-700 border border-violet-200" :
                        p.status?.startsWith("past_due") ? "bg-amber-50 text-amber-800 border border-amber-200" :
                        "bg-secondary"
                      }`}>{p.status}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          {filtered.length > PAGE_SIZE && (
            <Pagination page={safePage} pageCount={pageCount} onChange={setPage} testid="history-pager"/>
          )}
        </>
      ))}
    </div>
  );
}

/* ─── Pagination control ───────────────────────────────────────────── */
function Pagination({ page, pageCount, onChange, testid }) {
  return (
    <div className="px-5 py-3 border-t border-border flex items-center justify-between bg-secondary/20" data-testid={testid}>
      <div className="text-[11.5px] text-foreground/60 mono">
        Page {page + 1} of {pageCount}
      </div>
      <div className="flex items-center gap-1.5">
        <button onClick={() => onChange(0)} disabled={page === 0}
          data-testid={`${testid}-first`}
          className="px-2 h-8 rounded-md hover:bg-secondary text-foreground/70 disabled:opacity-30 disabled:hover:bg-transparent inline-flex items-center gap-0.5 text-[12px]">
          <ChevronsRight className="w-3.5 h-3.5 rotate-180"/>
        </button>
        <button onClick={() => onChange(page - 1)} disabled={page === 0}
          data-testid={`${testid}-prev`}
          className="px-2 h-8 rounded-md hover:bg-secondary text-foreground/70 disabled:opacity-30 disabled:hover:bg-transparent inline-flex items-center gap-0.5 text-[12px]">
          <ChevronLeft className="w-3.5 h-3.5"/>
        </button>
        <button onClick={() => onChange(page + 1)} disabled={page >= pageCount - 1}
          data-testid={`${testid}-next`}
          className="px-2 h-8 rounded-md hover:bg-secondary text-foreground/70 disabled:opacity-30 disabled:hover:bg-transparent inline-flex items-center gap-0.5 text-[12px]">
          <ChevronRight className="w-3.5 h-3.5"/>
        </button>
        <button onClick={() => onChange(pageCount - 1)} disabled={page >= pageCount - 1}
          data-testid={`${testid}-last`}
          className="px-2 h-8 rounded-md hover:bg-secondary text-foreground/70 disabled:opacity-30 disabled:hover:bg-transparent inline-flex items-center gap-0.5 text-[12px]">
          <ChevronsRight className="w-3.5 h-3.5"/>
        </button>
      </div>
    </div>
  );
}

/* ─── Request-limit-increase dialog ─────────────────────────────────── */
function RequestIncreaseDialog({ open, onClose, currentLimit, onSubmitted }) {
  const [amount, setAmount] = useState("");
  const [revenue, setRevenue] = useState("");
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) return;
    // Default to next "nice" round up from current limit
    const tiers = [50_000, 100_000, 250_000, 500_000, 1_000_000, 2_500_000, 5_000_000];
    const next = tiers.find(t => t > currentLimit) || currentLimit * 2;
    setAmount(String(next));
    setRevenue("");
    setNotes("");
  }, [open, currentLimit]);

  const submit = async () => {
    const n = Number(amount);
    if (!n || n <= currentLimit) return toast.error(`Must exceed current limit of ${money(currentLimit)}`);
    setSubmitting(true);
    try {
      const { data } = await api.post("/seller/credit/apply", {
        requested_limit_usd: n,
        monthly_revenue_usd: revenue ? Number(revenue) : null,
        notes: notes || null,
      });
      toast.success(`Sent to ${data.lender_org_name || "lender"} — usually decided within 1 business day`);
      onSubmitted();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(d?.message || d?.reason || "Request failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="sm:max-w-lg" data-testid="seller-credit-increase-dialog">
        <DialogHeader>
          <DialogTitle className="display text-xl">Request a higher limit</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 mt-2">
          <div className="rounded-xl bg-secondary/50 border border-border px-4 py-3 text-[13px]">
            Current limit: <span className="font-medium tabular-nums">{money(currentLimit)}</span>
          </div>
          <div>
            <Label>New limit (USD)</Label>
            <Input type="number" min={currentLimit + 1} step={10000} value={amount}
              onChange={e => setAmount(e.target.value)}
              data-testid="increase-amount"
              className="mt-1.5 h-11"/>
            <div className="mt-2 grid grid-cols-4 gap-1.5">
              {[100_000, 250_000, 500_000, 1_000_000].filter(p => p > currentLimit).slice(0, 4).map(p => (
                <button key={p} onClick={() => setAmount(String(p))}
                  data-testid={`increase-preset-${p}`}
                  className="text-[11px] mono rounded-md py-1.5 border border-border hover:bg-secondary">
                  {money(p)}
                </button>
              ))}
            </div>
          </div>
          <div>
            <Label>Monthly revenue (optional)</Label>
            <Input type="number" min={0} value={revenue}
              onChange={e => setRevenue(e.target.value)}
              placeholder="e.g. 500000"
              data-testid="increase-revenue"
              className="mt-1.5 h-11"/>
            <p className="text-[10.5px] text-foreground/45 mt-1">Helps the lender size the bump faster.</p>
          </div>
          <div>
            <Label>Notes (optional)</Label>
            <Input value={notes} onChange={e => setNotes(e.target.value)}
              placeholder="Anything the lender should know"
              data-testid="increase-notes"
              className="mt-1.5 h-11"/>
          </div>
          <div className="text-[11.5px] text-foreground/55">
            No hard credit check. Lender typically decides within 1 business day.
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="rounded-full">Cancel</Button>
          <Button onClick={submit} disabled={submitting}
            data-testid="increase-submit"
            className="rounded-full bg-foreground text-background hover:bg-foreground/90">
            {submitting ? "Submitting…" : "Submit request"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


/* ─── Phase SC-5.3 · Eligibility Gate panel ──────────────────────────── */
// Soft-gate keys that link directly to the right Settings row.
const SOFT_FIX_DEEPLINK = {
  w9_on_file:  { target: "/seller/settings?focus=w9#business",  label: "Upload W-9 in Settings" },
  coi_on_file: { target: "/seller/settings?focus=coi#business", label: "Upload COI in Settings" },
};

function EligibilityGatePanel({ gate, onChange }) {
  const nav = useNavigate();
  const hard = gate.gates.filter(g => g.kind === "hard");
  const soft = gate.gates.filter(g => g.kind === "soft");
  const hardFails = hard.filter(g => !g.ok);
  const softFails = soft.filter(g => !g.ok);
  const onlySoft = hardFails.length === 0 && softFails.length > 0;

  return (
    <div className={`rounded-2xl border p-6 ${onlySoft ? "border-amber-200 bg-amber-50" : "border-rose-200 bg-rose-50"}`}
         data-testid="seller-factoring-gate-panel">
      <div className="flex items-start gap-4">
        <Lock className={`w-5 h-5 mt-0.5 shrink-0 ${onlySoft ? "text-amber-600" : "text-rose-600"}`}/>
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <h3 className={`display text-lg ${onlySoft ? "text-amber-950" : "text-rose-950"}`}>
              {onlySoft ? "Factoring locked — finish onboarding to unlock" : "Factoring temporarily unavailable"}
            </h3>
            <span className={`chip text-[10px] uppercase tracking-wider ${onlySoft ? "bg-amber-100 text-amber-800 border-amber-200" : "bg-rose-100 text-rose-800 border-rose-200"}`}>
              {hardFails.length > 0 ? `${hardFails.length} hard` : ""}{hardFails.length > 0 && softFails.length > 0 ? " · " : ""}{softFails.length > 0 ? `${softFails.length} soft` : ""}
            </span>
          </div>
          <p className={`text-[12.5px] mt-1.5 leading-relaxed ${onlySoft ? "text-amber-900" : "text-rose-900"}`}>
            {onlySoft
              ? "Add the missing documents in Settings → Business details and factoring unlocks instantly — no score impact."
              : "One or more compliance checks need attention before you can factor invoices."}
          </p>

          <div className="mt-4 space-y-2">
            {[...hardFails, ...softFails].map(g => {
              const fix = SOFT_FIX_DEEPLINK[g.key];
              return (
                <div key={g.key}
                     data-testid={`gate-${g.key}`}
                     className="rounded-lg bg-white/70 border border-border px-3.5 py-2.5">
                  <div className="flex items-center gap-2 text-[12.5px]">
                    <AlertTriangle className={`w-3.5 h-3.5 shrink-0 ${g.kind === "hard" ? "text-rose-600" : "text-amber-600"}`}/>
                    <span className="font-medium text-foreground/85">{g.label}</span>
                    <span className={`ml-auto chip text-[9.5px] uppercase tracking-wider ${g.kind === "hard" ? "bg-rose-50 text-rose-700 border-rose-200" : "bg-amber-50 text-amber-700 border-amber-200"}`}>
                      {g.kind}
                    </span>
                    {fix && (
                      <button
                        type="button"
                        onClick={() => nav(fix.target)}
                        data-testid={`gate-fix-${g.key}`}
                        className="chip bg-foreground text-background text-[10px] uppercase tracking-wider hover:bg-foreground/85 transition-colors inline-flex items-center gap-1"
                      >
                        {fix.label}
                        <ChevronRight className="w-3 h-3"/>
                      </button>
                    )}
                  </div>
                  {g.detail && (
                    <div className="mt-1 text-[11.5px] text-foreground/55 pl-5.5">{g.detail}</div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}

/* ─── Phase SC-5.3 · Factoring Score breakdown panel ─────────────────── */
function FactoringScorePanel({ score }) {
  const [open, setOpen] = useState(false);
  const composite = Number(score.composite_score || 0);
  const tier = score.tier || "—";
  const subs = score.sub_scores || {};
  const tierColor = {
    excellent: "bg-emerald-50 text-emerald-700 border-emerald-200",
    good:      "bg-violet-50 text-violet-700 border-violet-200",
    fair:      "bg-amber-50 text-amber-800 border-amber-200",
    building:  "bg-slate-100 text-slate-700 border-slate-200",
    declined:  "bg-rose-50 text-rose-700 border-rose-200",
  }[tier] || "bg-secondary text-foreground/70 border-border";

  const bars = [
    { key: "sales_diversification", label: "Sales & Diversification", weight: 30 },
    { key: "fulfillment",           label: "Fulfillment Reliability", weight: 25 },
    { key: "claims_disputes",       label: "Claims & Disputes",        weight: 15 },
    { key: "financial_health",      label: "Financial Health",         weight: 30 },
  ];

  return (
    <div className="rounded-2xl bg-white border border-border overflow-hidden" data-testid="seller-factoring-score-panel">
      <button
        onClick={() => setOpen(o => !o)}
        data-testid="seller-factoring-score-toggle"
        className="w-full px-5 py-4 border-b border-border flex items-center justify-between text-left hover:bg-secondary/30 transition-colors"
      >
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-foreground to-foreground/85 text-background flex flex-col items-center justify-center">
            <Award className="w-4 h-4 text-accent"/>
          </div>
          <div>
            <div className="text-[10px] mono uppercase tracking-[0.2em] text-foreground/50">Factoring score</div>
            <div className="flex items-baseline gap-2 mt-0.5">
              <span className="display text-2xl tabular-nums" data-testid="seller-factoring-score-value">{composite.toFixed(0)}</span>
              <span className="text-[11.5px] text-foreground/45 mono">/ 850</span>
              <span className={`chip uppercase tracking-wider text-[10px] border ${tierColor}`} data-testid="seller-factoring-tier">{tier}</span>
              {score.thin_file && (
                <span className="chip bg-secondary border-border text-[9.5px] text-foreground/60" data-testid="seller-factoring-thin-file">thin file</span>
              )}
            </div>
            <p className="text-[11.5px] text-foreground/55 mt-0.5">Lender-side credit risk · independent of operational trust score</p>
          </div>
        </div>
        <div className="flex items-center gap-3 shrink-0">
          <Gauge className="w-4 h-4 text-foreground/40"/>
          {open ? <ChevronDown className="w-4 h-4 text-foreground/50"/> : <ChevronRight className="w-4 h-4 text-foreground/50"/>}
        </div>
      </button>

      {open && (
        <div className="px-5 py-5 space-y-4 bg-secondary/20">
          {bars.map(b => {
            const v = Number(subs[b.key] || 0);
            const pct = Math.max(0, Math.min(100, v));
            return (
              <div key={b.key} data-testid={`subscore-${b.key}`}>
                <div className="flex items-baseline justify-between mb-1">
                  <div className="text-[12.5px] text-foreground/75">{b.label}</div>
                  <div className="text-[11.5px] mono text-foreground/60 tabular-nums">{v.toFixed(1)}/100 · weight {b.weight}%</div>
                </div>
                <div className="h-1.5 rounded-full bg-secondary/80 overflow-hidden">
                  <div className="h-full rounded-full bg-foreground" style={{ width: `${pct}%` }}/>
                </div>
              </div>
            );
          })}

          <div className="grid grid-cols-3 gap-3 pt-2 border-t border-border">
            <Metric label="Starter line"    value={`$${(score.starter_limit_usd || 0).toLocaleString()}`}/>
            <Metric label="Advance rate"    value={`${Math.round((score.advance_rate || 0) * 100)}%`}/>
            <Metric label="Sample size"     value={`${score.sample_size || 0} invoices`}/>
          </div>
          <div className="text-[10.5px] text-foreground/45 mono">
            Methodology {score.methodology_version || "factoring_v1.0"} · computed {score.computed_at ? new Date(score.computed_at).toLocaleString() : "—"}
          </div>
        </div>
      )}
    </div>
  );
}

function Metric({ label, value }) {
  return (
    <div>
      <div className="text-[10px] mono uppercase tracking-[0.2em] text-foreground/45">{label}</div>
      <div className="display text-sm mt-0.5 tabular-nums">{value}</div>
    </div>
  );
}
